import { computed, inject, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, Observable, take } from 'rxjs';
import { ServiceProviderBrandingResponse } from '../../api/service-providers/models/api/service-provider-branding-response';
import { ICLEI_DOMAIN } from '../../app.constants';
import { ServiceProviderName } from '../../model/service-provider-name';
import { WINDOW } from '../../services/window.service';
import { UserTenant } from '../user/user.types';
import { EnrichedTenant, TenantsStore } from './tenants.store';

@Injectable({ providedIn: 'root' })
export class TenantsFacadeService {
  private _tenantsStore = inject(TenantsStore);
  private _window = inject<Window>(WINDOW);

  serviceProviderBranding: Signal<ServiceProviderBrandingResponse> = computed(
    () => this._tenantsStore.activeTenant()?.serviceProviderBranding ?? { logoUrl: '', supportEmail: '' }
  );
  serviceProviderName: Signal<ServiceProviderName> = computed(() => {
    const tenant = this._tenantsStore.activeTenant();
    if (!!tenant && 'serviceProviderName' in tenant) {
      return <ServiceProviderName>tenant.serviceProviderName;
    }

    return this.getFallbackServiceProviderName();
  });
  serviceProviderSupportEmail: Signal<string> = computed(
    () => this._tenantsStore.activeTenant()?.serviceProviderBranding?.supportEmail ?? ''
  );

  get activeTenant(): EnrichedTenant | null {
    return this._tenantsStore.activeTenant();
  }

  private _tenants$ = toObservable(this._tenantsStore.tenants);

  getEnrichedTenant$(tenant: UserTenant | { id: string }): Observable<EnrichedTenant> {
    if (this._tenantsStore.activeTenant()?.id !== tenant.id) {
      this._tenantsStore.loadTenant(tenant);
    }
    return this._tenants$.pipe(
      filter((tenants) => !!tenants[tenant.id]),
      map((tenants) => tenants[tenant.id]),
      take(1)
    );
  }

  getFallbackServiceProviderName(): ServiceProviderName {
    return this._window.location.href.includes(ICLEI_DOMAIN) ? 'ICLEI USA' : 'ClimateView AB';
  }
}
